import React, { useState, useEffect } from "react";
import * as PR from "../../../prime-modules/index";

const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const ContentBlockItem = ({ contentBlock, index, searchValue, contentBlockSaveHandler, editKey, setEditKey, moveUp, moveDown, lastIndex, loading, cancelAddNewContentBlock }) => {
    const [enableEdit, setEnableEdit] = useState(!contentBlock.title && !contentBlock.image && !contentBlock.link);
    const [title, setTitle] = useState(contentBlock.title || "");
    const [image, setImage] = useState(contentBlock.image || "");
    const [link, setLink] = useState(contentBlock.link || "");
    const [id] = useState(contentBlock.id);
    const [errors, setErrors] = useState({});
    const [deleteModal, setDeleteModal] = useState(false);

    useEffect(() => {
        if (!contentBlock.title && !contentBlock.image && !contentBlock.link) {
            setEnableEdit(true);
            setEditKey(id);
        }
    }, [contentBlock.title, contentBlock.image, contentBlock.link, id, setEditKey]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const base64 = await fileToBase64(file);
            setImage(base64);
        }
    };

    const saveHandler = () => {
        let newErrors = {};
        if (!title.trim()) newErrors.title = "Title is required";
        if (!image) newErrors.image = "Image is required";
        if (!link.trim()) newErrors.link = "Link is required";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        contentBlockSaveHandler({ title, image, link, id }, index);
        setEnableEdit(false);
        setEditKey("");
    };

    const cancelHandler = () => {
        if (!contentBlock.title && !contentBlock.image && !contentBlock.link) {
            cancelAddNewContentBlock();
        } else {
            setTitle(contentBlock.title || "");
            setImage(contentBlock.image || "");
            setLink(contentBlock.link || "");
            setEnableEdit(false);
            setEditKey("");
        }
        setErrors({});
    };

    const deleteHandler = () => {
        contentBlockSaveHandler(null, index, 'delete');
        setDeleteModal(false);
    };

    const isVisible = !searchValue || 
        title.toLowerCase().includes(searchValue.toLowerCase()) || 
        link.toLowerCase().includes(searchValue.toLowerCase());

    if (!isVisible) return null;

    return (
        <div className={`content-block-item ${enableEdit ? "content-block-editing" : ""} ${loading ? "loading-state" : ""}`}>
            <div className="grid">
                {/* Title & Link side by side */}
                <div className="col-6">
                    <label className="mb-2">Title</label>
                    <PR.InputText
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Content Block Title"
                        className='title-input-field'
                        disabled={!enableEdit}
                    />
                    {errors.title && <div className='error-msg'>{errors.title}</div>}
                </div>
                <div className="col-6">
                    <label className="mb-2">Link</label>
                    <PR.InputText
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        placeholder="Content Block Link (URL)"
                        className='title-input-field'
                        disabled={!enableEdit}
                    />
                    {errors.link && <div className='error-msg'>{errors.link}</div>}
                </div>

                {/* Image row — label + preview side by side */}
                <div className="col-12 mt-1">
                    <div className="content-block-image-row">
                        <label className="content-block-image-label">Image <span className="image-note">(upload 700px x 500px for better view)</span></label>
                        <div className="content-block-image-content">
                            {enableEdit && (
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="file-upload-input"
                                />
                            )}
                            {image && (
                                <img
                                    src={image.startsWith('data:') ? image : `data:image/png;base64,${image}`}
                                    alt="Content Block Preview"
                                    className="content-block-image-thumb"
                                />
                            )}
                        </div>
                    </div>
                    {errors.image && <div className='error-msg'>{errors.image}</div>}
                </div>
            </div>

            {/* Buttons — left: Delete/Edit, right: Up/Down */}
            <div className="grid grid-nogutter align-items-center mt-3">
                <div className="col-12 content-block-buttons">
                    {!enableEdit ? (
                        <>
                            <div className="content-block-btn-left">
                                <PR.Button label='Delete' className="content-block-btn outline-button" disabled={editKey} onClick={() => setDeleteModal(true)} />
                                <PR.Button label="Edit" className="content-block-btn add-category-btn" disabled={editKey && editKey !== id} onClick={() => { setEnableEdit(true); setEditKey(id); }} />
                            </div>
                            <div className="content-block-btn-right">
                                <PR.Button icon="pi pi-arrow-up" className="content-block-btn outline-button" onClick={() => moveUp(index)} disabled={editKey || index === 0} />
                                <PR.Button icon="pi pi-arrow-down" className="content-block-btn outline-button" onClick={() => moveDown(index)} disabled={editKey || index === lastIndex} />
                            </div>
                        </>
                    ) : (
                        <div className="content-block-btn-left">
                            <PR.Button label='Cancel' className="content-block-btn outline-button" onClick={cancelHandler} />
                            <PR.Button label="Save" className="content-block-btn add-category-btn" onClick={saveHandler} />
                        </div>
                    )}
                </div>
            </div>

            <PR.Dialog
                header="Delete Content Block"
                visible={deleteModal}
                draggable={false}
                resizable={false}
                onHide={() => setDeleteModal(false)}
                className="delete-confirm-modal"
            >
                <div className="delete-confirm-content">
                    <p>Are you sure you want to delete this content block?</p>
                    <div className="text-right">
                        <PR.Button label='Cancel' onClick={() => setDeleteModal(false)} className="outline-button" />
                        <PR.Button label="Delete" className="add-category-btn" onClick={deleteHandler} />
                    </div>
                </div>
            </PR.Dialog>
        </div>
    );
};

export default ContentBlockItem;
